import { Slot } from "radix-ui";
import type { ComponentPropsWithRef } from "react";
import { cn } from "@/core/shared/v2/lib/utils";

export interface BadgeProps extends ComponentPropsWithRef<"span"> {
  variant?: "primary" | "whiteLinen" | "teal" | "neutral" | "success" | "warning" | "destructive";
  size?: "default" | "sm" | "xs";
  asChild?: boolean;
}

export function Badge({ className, variant = "primary", size = "default", asChild = false, ...props }: BadgeProps) {
  const Comp = asChild ? Slot.Root : "span";

  const baseStyles =
    "inline-flex w-fit shrink-0 items-center justify-center gap-1 overflow-hidden whitespace-nowrap rounded-full font-medium text-sm transition-[color,box-shadow] focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 [&>svg]:pointer-events-none [&>svg]:size-3";

  const variantStyles = {
    primary: "bg-primary text-white",
    whiteLinen: "bg-white-linen-100 text-dodger-blue-700",
    teal: "bg-teal-500 text-white",
    neutral: "bg-muted text-muted-foreground",
    success: "bg-success-100 text-success-800",
    warning: "bg-warning-100 text-warning",
    destructive: "bg-error-100 text-error-700",
  };

  const sizeStyles = {
    default: "px-3 py-2",
    sm: "px-2 py-0.5",
    xs: "px-1 py-0.5 text-xs",
  };

  return (
    <Comp
      data-slot="badge"
      className={cn(baseStyles, variantStyles[variant], sizeStyles[size], className)}
      {...props}
    />
  );
}
